import {Box, Flex, Heading, Text, Show, Button} from '@chakra-ui/react';
import {GetStaticProps} from 'next';
import Image from 'next/image';
import {ResultOf} from '@graphql-typed-document-node/core';
import mixpanel from 'mixpanel-browser';
import NextLink from 'next/link';
import {TitleMetaLinkTag} from 'react-datocms';
import {useRef, useState} from 'react';
import {useInView} from 'framer-motion';
import {BANKSY_FAQ_CONTENT, FAQ_CONTENT, FAQs} from '../components/global/faq';
import {PageLayout} from '../components/global/page-layout';
import {useUserQuery} from '../graphql/generated';
import {Timeline as HIWTimeline} from '../components/how-it-works/timeline';
import {Timeline} from '../components/banksy-page/timeline-slider';
import {DetailsSection} from '../components/banksy-page/details-section';
import {KeyDocumentRow} from '../components/banksy-page/key-document-row';
// import {ShowpieceCardBanner} from '../components/showpiece-card/showpiece-card-banner';
import {request} from '../graphql/api';
import {VdmPageDocument} from '../graphql/datocms-generated';
import {BasicCardCarousel} from '../components/banksy-page/basicCardCarousel';
import {BasicCard} from '../components/banksy-page/basicCard';
import {BanksyHero} from '../components/banksy-page/banksyHero';
import {BanksyHeroMobile} from '../components/banksy-page/banksyHeroMobile';
import {SlideInBox} from '../components/banksy-page/slide-in-box';
import {useCurrentAssetOverview} from '../hooks/current-asset-overview';
import {ProductFooterLoader} from '../components/product/product-footer-loader';
import {BanksyNftBanner} from '../components/banksy-page/banksyNftBanner';
import {ExperienceBanksy} from '../components/banksy-page/experienceBanksy';
import {RefugeCharity} from '../components/banksy-page/refugeCharity';
import {ArtWithPurpose} from '../components/banksy-page/artWithPurpose';
import {OasisCharity} from '../components/banksy-page/oasisCharity';
import {BenefitsSection} from '../components/banksy-page/benefits-section';

interface VDMLayoutProps {
  pageData: ResultOf<typeof VdmPageDocument>['valentinesDayMascaraPage'];
}

function Page({pageData}: VDMLayoutProps): JSX.Element {
  const asset = useCurrentAssetOverview('valentines.day.mascara');
  const userQuery = useUserQuery();

  const contentRef = useRef(null);
  const contentInView = useInView(contentRef);
  const footerRef = useRef(null);
  const footerInView = useInView(footerRef);

  const [showProductFooter, setShowProductFooter] = useState<boolean>(false);

  return (
    <PageLayout
      firstName={userQuery.data?.user?.firstName}
      hideShapes
      additionalContentStyles={{paddingTop: '0!important'}}
      seo={pageData?.seo as TitleMetaLinkTag[]}
      FooterBanner={
        contentInView || footerInView || showProductFooter ? (
          <ProductFooterLoader
            assetId={asset?.assetId || ''}
            color="aqua.500"
            purchaseUrl="own-a-piece/valentines-day-mascara"
            price={asset.priceString}
            darkMode
          />
        ) : (
          <></>
        )
      }
    >
      <Show above="md">
        <BanksyHero
          minHeight={100}
          mainBackdrop="/assets/banksy/bansky-desktop-image.jpg"
          midBackdrop="/assets/banksy/banksy.jpg"
          video="/assets/banksy/banksy-montage.mp4"
          setShowProductFooter={setShowProductFooter}
        />
      </Show>
      <Show below="md">
        <BanksyHeroMobile
          mainBackdrop="/assets/banksy/banksey-desk-preview.png"
          midBackdrop="/assets/banksy/banksy-mobile.png"
        />
      </Show>
      <Box
        backgroundColor="#00061A"
        width="100%"
        overflow="hidden"
        ref={contentRef}
      >
        <Box px={{base: 0, md: '24px'}}>
          <DetailsSection />
        </Box>
        <Box mt={{base: '32px', md: '24px'}}>
          <Heading size="lg" as="h2" textAlign="center" color="white">
            Gallery
          </Heading>
          <BasicCardCarousel
            cards={pageData?.gallery.map((card) => (
              <Box px="30px">
                <BasicCard
                  enableAnimation
                  image={card.image?.responsiveImage}
                  imageTag={card.imageTag}
                  text={card.text}
                />
              </Box>
            ))}
            enableAnimation
            cardTypeName="Product Card"
            cardWidth={335}
          />
        </Box>
        <BenefitsSection />
        <Box>
          <Timeline
            title="Timeline of the piece"
            steps={[
              {
                date: '14 February 2023',
                title: 'Artwork discovered on Margate residential street.',
                content:
                  'Residents of Margate were delighted to learn that Banksy chose their neighbourhood to daub a new piece of poignant art timed for Valentine’s Day.',
              },
              {
                date: '14 February 2023',
                title: 'Thanet council remove freezer citing safety concerns.',
                content:
                  'In an uncharacteristically fast response to clearing rubbish, Thanet council removed the freezer and other items from the site. This prompted an outcry from residents who called it overreach by their local government at the expense of art that could draw tourists to Margate, Kent.',
              },
              {
                date: '15 February 2023',
                title:
                  'Freezer returned to is originally position after being deemed safe by the council.',
                content:
                  'Thanet council return freezer and frying pan to the artwork. ',
              },
              {
                date: '30 March 2023',
                title:
                  'Valentine’s Day Mascara Wall safely removed so that the artwork can be conserved ',
                content:
                  'Work is undertaken to safely remove Valentines day mascara from the property. Steel beams are used to support structure as the ‘wall’ is lifted onto a lorry and sent to art conservationist Thomas Organ',
              },
            ]}
          />
        </Box>
        <BanksyNftBanner
          desktopImage="/assets/banksy/banksey-nft.png"
          mobileImage="/assets/banksy/banksey-nft-mobile.png"
        />
        <Box color="white" mb={{base: '20px', sm: '40px', md: 0}}>
          <ExperienceBanksy />
        </Box>
        <Flex
          maxW="1120px"
          width="100%"
          margin="0 auto"
          pt={{base: '30px', md: '60px'}}
          px={{base: '20px', md: 0}}
          flexDirection={{base: 'column-reverse', md: 'row'}}
          color="white"
        >
          <Box
            position="relative"
            flex={1}
            minHeight="767px"
            display={{base: 'none', md: 'block'}}
          >
            <Box
              height="767px"
              width="753px"
              position="absolute"
              top={0}
              right={0}
            >
              <Image src="/assets/dreamland-wheel.png" layout="fill" />
            </Box>
          </Box>
          <Flex
            flexDirection="column"
            ml={{base: 0, md: '64px'}}
            flex={1}
            pt={{base: 0, md: '96px'}}
            pb={{base: 0, md: '192px'}}
            maxW="520px"
            position="relative"
            minHeight={{base: '500px', md: 'auto'}}
          >
            <SlideInBox>
              <Heading
                fontSize={{base: '40px', md: '60px'}}
                bgGradient="linear(148deg, #23EBE5 16.66%, #DE69C3 33.93%, #E32E8A 56.30%, #DC7324 89.07%)"
                bgClip="text"
                zIndex={5}
                alignSelf="flex-start"
              >
                DREAMLAND
              </Heading>
            </SlideInBox>
            <SlideInBox>
              <Heading mb={{base: '16px', md: '24px'}} zIndex={5}>
                in Margate
              </Heading>
            </SlideInBox>
            <Text mb={{base: '20px', md: 0}} zIndex={5}>
              Considered to be one of the oldest-surviving amusement parks in
              Great Britain, the site of Dreamland dates back to the British
              railway boom of the early 1870s. Today it is a hub of culture on
              the south east coast, with its music venue and art installations.
              After its removal, ‘Valentine’s Day Mascara’ found a new home at
              Dreamland. This means you will be able to see the wall on display
              all year round.
            </Text>

            <Box
              height="500px"
              width="491px"
              position="absolute"
              top="160px"
              right="-20px"
              display={{base: 'block', md: 'none'}}
            >
              <Box
                position="absolute"
                width="100%"
                height="100%"
                zIndex={4}
                bgGradient="linear(to-b, rgba(0, 6, 26, 0.7) 20%, rgba(0, 6, 26, 0)) 100%"
              />
              <Image src="/assets/dreamland-wheel.png" layout="fill" />
            </Box>
          </Flex>
        </Flex>
        <Flex
          maxW="1120px"
          width="100%"
          margin="0 auto"
          pt={{base: '30px', md: 0}}
          px={{base: '20px', md: 0}}
          flexDirection={{base: 'column-reverse', md: 'row'}}
          color="white"
        >
          <Flex
            flexDirection="column"
            mr={{base: 0, md: '64px'}}
            flex={1}
            pt={{base: 0, md: '96px'}}
            pb={{base: 0, md: '132px'}}
            maxW="520px"
            position="relative"
            minHeight={{base: '500px', md: 'auto'}}
          >
            <SlideInBox>
              <Heading zIndex={5}>Why</Heading>
            </SlideInBox>
            <SlideInBox>
              <Heading
                fontSize={{base: '40px', md: '60px'}}
                zIndex={5}
                alignSelf="flex-start"
                mb={{base: '16px', md: '24px'}}
                color="aqua.700"
              >
                Banksy?
              </Heading>
            </SlideInBox>

            <Text zIndex={5}>
              Our collection spans 157 years of unique, culturally significant
              collectibles, with the youngest piece to date (Reigning Queens HC
              3/3) a spritely 38 years young. Now, Valentine’s Day Mascara
              brings this collection into the present day. Banksy has graffitied
              his place into history as a movement defining artist,
              communicating bold messaging, with an unmistakable style. We have
              no doubt this piece is already the definition of a collectible
              item, but its provenance will only grow as Banksy’s story unfolds.
            </Text>
            <Box mt="20px" mb={{base: '182px', md: 0}} zIndex={5}>
              {asset.status === 'COMING_SOON' ? (
                <NextLink href="/banksy-waitlist">
                  <Button
                    colorScheme="aqua"
                    width="215px"
                    onClick={() => {
                      mixpanel.track(
                        'Banksy product page sign up to waitlist click',
                      );
                    }}
                  >
                    Sign up to waitlist
                  </Button>
                </NextLink>
              ) : (
                <NextLink href="/own-a-piece/valentines-day-mascara">
                  <Button
                    colorScheme="aqua"
                    width="215px"
                    onClick={() => {
                      mixpanel.track('Banksy product page buy click');
                    }}
                  >
                    Buy
                  </Button>
                </NextLink>
              )}
            </Box>
            <Box
              height="287px"
              width="250px"
              position="absolute"
              bottom={0}
              right={0}
              display={{base: 'block', md: 'none'}}
              bgGradient="linear(to-b, rgba(0, 6, 26, 0.7), rgba(0, 6, 26, 0))"
              zIndex={4}
            />
            <Box
              height="287px"
              width="250px"
              position="absolute"
              bottom={0}
              right={0}
              display={{base: 'block', md: 'none'}}
            >
              <Image src="/assets/einstein-picket-cropped.png" layout="fill" />
            </Box>
          </Flex>
          <Box
            position="relative"
            flex={1}
            minHeight="500px"
            display={{base: 'none', md: 'block'}}
          >
            <Box height="770px" width="510px" position="absolute" bottom={0}>
              <Box position="absolute" width="100%" height="100%" zIndex={4} />
              <Image src="/assets/einstein-picket.png" layout="fill" />
            </Box>
          </Box>
        </Flex>
      </Box>
      <Box ref={footerRef}>
        <Flex
          maxW="1120px"
          width="100%"
          margin="0 auto"
          my={{base: '40px', md: '80px'}}
          px={{base: '20px', md: 0}}
          flexDirection="column"
        >
          <Heading size="xl" fontWeight="600" mb="28px">
            Key documents
          </Heading>
          {pageData?.documents?.map((document) => (
            <KeyDocumentRow
              documentName={document.documentName || ''}
              url={document.document?.url || ''}
            />
          ))}
        </Flex>
        <ArtWithPurpose />
        <RefugeCharity />
        <OasisCharity />
        {/* {pageData?.relatedProducts &&
          pageData.relatedProducts.map((item) => (
            <Box pb={{base: '16px', md: '40px'}}>
              <ShowpieceCardBanner
                items={item.selectedAssets as any}
                heading={item.heading || ''}
                subheading={item.productCarouselSubheading || ''}
              />
            </Box>
          ))} */}
        <Flex
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          mt="48px"
        >
          <Heading fontWeight="600">How Showpiece Works</Heading>
          <Heading
            size="sm"
            textAlign="center"
            display={{base: 'block', md: 'none'}}
            mt="24px"
          >
            Real ownership. Tangible items. Proven provenance.
          </Heading>
          <Heading
            size="sm"
            textAlign="center"
            display={{base: 'block', md: 'none'}}
            mt="16px"
          >
            With the support of our expert partners we scour the globe for
            iconic collectables with Incredible stories, documented provenance
            and a track record of desirability.
          </Heading>
          <Box
            maxW={{base: '500px', lg: '100%'}}
            mt={{base: '56px', md: '-24px'}}
          >
            <HIWTimeline />
          </Box>
        </Flex>
        <Box>
          <FAQs
            title="Frequently Asked Questions"
            faqs={[...BANKSY_FAQ_CONTENT, ...FAQ_CONTENT]}
          />
        </Box>
      </Box>
    </PageLayout>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  const pageData = await request(VdmPageDocument);

  return {
    revalidate: 10,
    props: {
      pageData: pageData.valentinesDayMascaraPage,
    },
  };
};

export default Page;
